import React, { useState, useEffect, useContext } from "react";
import GameExecution from "./pages/GameExecution";
import Dashboard from "./pages/Dashboard";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Sidebar from "./components/Sidebar";
import AutomationManagement from "./components/AutomationManagement/AutomationManagement";
import ExecuteTestcaseModal from "./components/ExecuteTestcaseModal";
import TestcaseManagement from "./components/TestcaseManagement";
import ExecutedTestcases from "./components/ExecutedTestcases";
import Login from "./pages/Login";
import SeoDashboard from "./pages/SeoDashboard";
import SeoReports from "./pages/SeoReports";
import { AuthContext } from "./AuthContext";
import "./index.css";
import UserRestrictionsManagement from "./components/UserRestrictionsManagement/UserRestrictionsManagement";

function App() {
    const { isLoggedIn, roles, login } = useContext(AuthContext);
    const [activePage, setActivePage] = useState("dashboard");

    useEffect(() => window.scrollTo(0, 0), [activePage]);

    if (!isLoggedIn) {
        return <Login onLogin={(token) => login(token)} />;
    }

    const hasRole = (requiredRoles = []) => roles.some((r) => requiredRoles.includes(r));

    const findFirstAllowed = () => {
        const candidateOrder = [
            { id: "userRestrictions", roles: ["SuperAdmin"] },
            { id: "dashboard", roles: ["SuperAdmin", "Admin", "Automation_Tester", "Team_Lead"] },
            { id: "seoResults", roles: ["SuperAdmin", "Admin", "Automation_Tester", "Team_Lead", "Tester"] },
            { id: "seoReports", roles: ["SuperAdmin", "Admin", "Automation_Tester", "Team_Lead", "Tester"] },
            { id: "gameExecution", roles: ["Tester","Automation_Tester","Team_Lead","Admin","SuperAdmin","App_Support","Product_Team"] },
            { id: "automationManagement", roles: ["Automation_Tester","Team_Lead","Admin","SuperAdmin"] },
            { id: "TestcaseManagement", roles: ["Automation_Tester","Tester","Team_Lead","Admin","SuperAdmin"] },
            { id: "ExecuteTestcaseModal", roles: ["Automation_Tester","Tester","Team_Lead","Admin","SuperAdmin"] },
            { id: "ExecutedTestcases", roles: ["Team_Lead","Admin","SuperAdmin"] },
        ];
        for (let candidate of candidateOrder) {
            if (hasRole(candidate.roles)) return candidate.id;
        }
        return "";
    };

    const renderPage = () => {
        switch (activePage) {
            case "userRestrictions":
                return hasRole(["SuperAdmin"]) ? (
                    <UserRestrictionsManagement />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "dashboard":
                return hasRole(["SuperAdmin","Admin","Automation_Tester","Team_Lead"]) ? (
                    <Dashboard />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "seoResults":
                return hasRole(["SuperAdmin","Admin","Automation_Tester","Team_Lead","Tester"]) ? (
                    <SeoDashboard />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "seoReports":
                return hasRole(["SuperAdmin","Admin","Automation_Tester","Team_Lead","Tester"]) ? (
                    <SeoReports />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "gameExecution":
                return hasRole(["Tester","Automation_Tester","Team_Lead","Admin","SuperAdmin","App_Support","Product_Team"]) ? (
                    <GameExecution />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "automationManagement":
                return hasRole(["Automation_Tester","Team_Lead","Admin","SuperAdmin"]) ? (
                    <AutomationManagement />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "TestcaseManagement":
                return hasRole(["Automation_Tester","Tester","Team_Lead","Admin","SuperAdmin"]) ? (
                    <TestcaseManagement />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "ExecuteTestcaseModal":
                return hasRole(["Automation_Tester","Tester","Team_Lead","Admin","SuperAdmin"]) ? (
                    <ExecuteTestcaseModal />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            case "ExecutedTestcases":
                return hasRole(["Team_Lead","Admin","SuperAdmin"]) ? (
                    <ExecutedTestcases />
                ) : (
                    <AccessDeniedPage goBack={() => setActivePage(findFirstAllowed())} />
                );
            default:
                return (
                    <div className="text-center text-2xl font-semibold text-red-500">
                        Page Not Found
                    </div>
                );
        }
    };

    return (
        <>
            <Header />
            <Sidebar activePage={activePage} setActivePage={setActivePage} />
            <main
                className="overflow-auto bg-gray-50 min-h-screen"
                style={{
                    marginTop: 64,    // header height
                    marginLeft: 256,  // sidebar width
                    paddingBottom: 64 // room for footer
                }}
            >
                {renderPage()}
            </main>
            <Footer />
        </>
    );
}

export default App;

const AccessDeniedPage = ({ goBack }) => (
    <div className="max-w-lg mx-auto mt-20 bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-3xl font-bold text-red-600 mb-4 text-center">
            Access Denied
        </h2>
        <p className="text-gray-700 text-center mb-6">
            You do not have permission to view this page.
        </p>
        <div className="flex justify-center">
            <button
                onClick={goBack}
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
            >
                Return to Available Page
            </button>
        </div>
    </div>
);
